import React, { useEffect } from 'react';
import proptypes from 'prop-types';
import {
  params,
  shape,
  arrayOf,
  namedFragment,
  typename,
  string as stringType,
  customType,
  bool as boolType,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { RichTextWrapper } from './subcomponents/RichTextWrapper';
import { ImageWrapper } from './subcomponents/ImageWrapper';
import { useNewspaper } from '../hooks/useNewspaper';

const { string, bool, number, shape: shapeType } = proptypes;

const NewspaperModule = ({
  id = '',
  componentClass = 'Newspaper',
  slotContentId: slotName = '',
  mediaCollectionIndex = 0,
  lazyLoad = false,
  livePreviewData = null,
}) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('newspaper-module.ready');
  }, []);

  // New dataModel call for slot-based fetching (fusion pattern)
  // Note: Using network-first since cache-only isn't working as expected in this version
  const {
    data: slotData,
    loading: slotLoading,
    error: slotError,
  } = useDataModel('slot', {
    variables: { id: slotName },
    fetchPolicy: 'cache-only',
    skip: !slotName, // Skip if slotName is empty/falsy
  });

  const {
    hasData,
    ref,
    clickID,
    imageRenderObj,
    richTextObj,
    sourceData

  } = useNewspaper(
    {
      id,
      slotId: slotData?.slot?.mediaCollections[mediaCollectionIndex]?.entryId,
      componentClass,
      livePreviewData,
      slotName,
      slotData,
      mediaCollectionIndex
    });

  if (slotLoading || slotError) {
    return null;
  }

  if (!hasData) {
    return null;
  }

  return (
    <ImpressionProvider
      data={{
        id: id || slotData?.slot?.mediaCollections[mediaCollectionIndex]?.entryId,
        name: 'NewspaperModule',
        component: 'NewspaperModule',
        type: 'content',
      }}
    >
      <div
        className="sui-grid sui-grid-cols-12"
        data-component="NewspaperModule"
        ref={ref}
        /* eslint-disable-next-line react/no-unknown-property */
        clickid={clickID}
      >
        {richTextObj?.richText && (
          <RichTextWrapper
            richTextObj={richTextObj}
            id={id || slotData?.slot?.mediaCollections[mediaCollectionIndex]?.entryId}
            isLivePreview={!!livePreviewData}
          />
        )}
        <ImageWrapper
          imageRenderObj={imageRenderObj}
          id={id || slotData?.slot?.mediaCollections[mediaCollectionIndex]?.entryId}
          lazyLoad={lazyLoad}
          sourceData={sourceData}
        />
      </div>
    </ImpressionProvider>
  );
};

NewspaperModule.propTypes = {
  id: string,
  componentClass: string,
  slotContentId: string,
  mediaCollectionIndex: number,
  lazyLoad: bool,
  livePreviewData: shapeType({}),
};

NewspaperModule.displayName = 'NewspaperModule';

// Do not touch
const DamMediaFragment = namedFragment({
  inline: false,
  fragmentType: 'DamMedia',
  fragmentAlias: 'DamMediaV1',
}).shape({
  damContentSelector: shape({
    assetData: arrayOf(
      shape({
        selectedImageUrl: stringType(),
      }),
    ),
  }),
  damDownloadedContent: shape({
    url: stringType(),
  }),
  imageHotspot: shape({
    id: stringType(),
    componentName: stringType(),
  }),
  id: stringType(),
  __typename: typename('DamMedia'),
});

NewspaperModule.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass')
      .enum(['Newspaper'])
      .isRequired(),
  }).shape({
    Newspaper: namedFragment({
      inline: true,
      fragmentType: 'Newspaper',
    }).shape({
      id: stringType(),
      altText: stringType(),
      link: stringType(),
      description: stringType(),
      damImageAlignmentBehavior: stringType(),
      damImageWidthBehavior: stringType(),
      descriptionAlignment: stringType(),
      desktopImage: DamMediaFragment,
      mobileImage: DamMediaFragment,
    }),
  }),
  slot: params({
    id: stringType(),
  }).shape({
    id: stringType(),
    entryId: stringType(),
    mediaCollections: arrayOf(
      shape({
        entryId: stringType(),
        media: arrayOf(
          shape({
            entryId: stringType(),
            richText: stringType(),
            customProps: stringType(),
            sourceDesktop: arrayOf(
              shape({
                entryId: stringType(),
                uri: stringType(),
                hyperlink: stringType(),
                altText: stringType(),
                hotspot: stringType(),
              })
            ),
            sourceMobile: arrayOf(
              shape({
                entryId: stringType(),
                uri: stringType(),
                hyperlink: stringType(),
                altText: stringType(),
                hotspot: stringType(),
              })
            ),
          }),
        ),
      }),
    ),
    __typename: typename('newSlot'),
  }),
};

export { NewspaperModule };
